import { type PropsWithChildren, useEffect, useState } from "react";

import { Box } from "../../../mads";

/**
 * Web keyboard-avoidance for the chat screen.
 *
 * The native sibling ({@link ./ScreenKeyboardAvoidingView.tsx}) relies on the
 * React Native `Keyboard` API, whose events never fire under react-native-web —
 * so on mobile web the composer would stay hidden behind the on-screen
 * keyboard. Browsers instead expose the on-screen keyboard through
 * `window.visualViewport`, which shrinks from the bottom when the keyboard opens
 * (iOS Safari and Android Chrome alike).
 *
 * We measure how much of the layout viewport the keyboard overlaps and lift the
 * whole screen by that amount via `paddingBottom`, matching the native behavior
 * of moving the input above the keyboard. On desktop web there is no on-screen
 * keyboard, the visual viewport never shrinks, the overlap stays 0, and this is
 * effectively a no-op.
 */
export function ScreenKeyboardAvoidingView({
  safeAreaBottomInset,
  children,
}: PropsWithChildren<{ safeAreaBottomInset?: number }>) {
  const [keyboardOverlap, setKeyboardOverlap] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => {
      // How much of the layout viewport the on-screen keyboard covers.
      // On iOS the layout viewport (window.innerHeight) stays full while the
      // visual viewport shrinks; on Android that behaves the same when the
      // browser resizes the visual viewport. When the browser instead resizes
      // the layout viewport (Android `resizes-content`), both shrink together
      // and this correctly evaluates to ~0 — the container already got shorter.
      const overlap = window.innerHeight - viewport.height - viewport.offsetTop;
      setKeyboardOverlap(Math.max(0, overlap - (safeAreaBottomInset ?? 0)));
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [safeAreaBottomInset]);

  return <Box sx={{ flex: 1, paddingBottom: keyboardOverlap }}>{children}</Box>;
}
